import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    ChecklistDeploy,
    ChecklistKind,
    ChecklistState,
    ChecklistStep,
    tickChecklistStep,
} from '../../api/rollout';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useRolloutChecklist } from '../../hooks/useRollout';
import { ago, span } from './rolloutModel';
import { CLAUDE_FOLLOW_PROMPT, CLAUDE_FOLLOW_STEPS } from './claudeFollow';
import './Rollout.scss';

const PHASES: { id: ChecklistStep['phase']; label: string; plain: string }[] = [
    { id: 'before', label: 'Voor de deploy', plain: 'Stappen' },
    { id: 'deploy', label: 'Deploy', plain: 'Deploy' },
    { id: 'after', label: 'Na de deploy', plain: 'Daarna' },
    { id: 'followup', label: 'Nazorg', plain: 'Later' },
];

const KIND: Record<ChecklistKind, string> = {
    Auto: 'Automatisch',
    Mcp: 'MCP',
    Manual: 'Handmatig',
};

const STATE: Record<ChecklistState, string> = {
    Done: 'Klaar',
    Open: 'Open',
    Warn: 'Let op',
    Bad: 'Niet in orde',
    Waiting: 'Later',
    Unknown: 'Niet gemeten',
};

const STATUS: Record<ChecklistDeploy['status'], string> = {
    open: 'Open',
    wacht: 'Wacht',
    bezig: 'Bezig',
    klaar: 'Klaar',
};

const ICON: Record<ChecklistState, string> = {
    Done: '✓',
    Open: '',
    Warn: '!',
    Bad: '✕',
    Waiting: '…',
    Unknown: '?',
};

const useNow = () => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 10000);

        return () => clearInterval(timer);
    }, []);

    return now;
};

export const checklistPath = (deployId?: string) =>
    deployId
        ? `/rollout/checklist/${encodeURIComponent(deployId)}`
        : '/rollout/checklist';

/** "wo 4 nov" from "2026-11-04". */
const day = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });

const StepState = ({ state }: { state: ChecklistState }) => (
    <span
        className={`ro-cl-state ro-cl-state--${state.toLowerCase()}`}
        role="img"
        aria-label={STATE[state]}
        title={STATE[state]}
    >
        {ICON[state]}
    </span>
);

/** One step: tick box, state, title with its kind, what was measured, how, and the tick. */
const StepRow = ({
    step,
    busy,
    now,
    onTick,
}: {
    step: ChecklistStep;
    busy: boolean;
    now: number;
    onTick: (done: boolean, note?: string) => void;
}) => {
    const [noting, setNoting] = useState(false);
    const [note, setNote] = useState('');
    const ticked = !!step.tick;

    const confirm = () => {
        onTick(true, note);
        setNoting(false);
        setNote('');
    };

    return (
        <li className={`ro-cl-step ro-cl-step--${step.state.toLowerCase()}`}>
            <input
                type="checkbox"
                className="ro-cl-step__box"
                checked={ticked}
                disabled={busy}
                aria-label={ticked ? 'Vinkje weghalen' : 'Afvinken'}
                onChange={() => (ticked ? onTick(false) : setNoting(true))}
            />
            <StepState state={step.state} />
            <div className="ro-cl-step__body">
                <div className="ro-cl-step__title">
                    <span>{step.title}</span>
                    <span
                        className={`ro-pill ro-cl-kind ro-cl-kind--${step.kind.toLowerCase()}`}
                    >
                        {KIND[step.kind]}
                    </span>
                    {step.ticket && (
                        <span className="ro-pill">#{step.ticket}</span>
                    )}
                </div>
                {step.detail && (
                    <div className="ro-cl-step__detail">{step.detail}</div>
                )}
                {step.how && !ticked && (
                    <div className="ro-cl-step__how">{step.how}</div>
                )}
                {step.tick && (
                    <div className="ro-cl-step__tick">
                        {step.tick.by ?? 'onbekend'} · {ago(step.tick.at, now)}
                        {step.tick.note && <> · {step.tick.note}</>}
                    </div>
                )}
                {noting && (
                    <div className="ro-cl-step__note">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Notitie (run-id, bevinding)"
                            value={note}
                            autoFocus
                            onChange={(e) => setNote(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') confirm();
                                if (e.key === 'Escape') setNoting(false);
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={confirm}
                        >
                            Afvinken
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setNoting(false)}
                        >
                            Annuleren
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
};

/** A deploy in the list on the left: status, title, progress. */
const DeployItem = ({
    deploy,
    active,
    next,
}: {
    deploy: ChecklistDeploy;
    active: boolean;
    next: boolean;
}) => (
    <Link
        to={checklistPath(deploy.id)}
        className={`ro-cl-deploy${active ? ' is-active' : ''}`}
        aria-current={active ? 'page' : undefined}
    >
        <span className="ro-cl-deploy__head">
            <strong>{deploy.title}</strong>
            <span className={`ro-pill ro-cl-status--${deploy.status}`}>
                {next && deploy.status !== 'klaar'
                    ? 'Volgende'
                    : STATUS[deploy.status]}
            </span>
        </span>
        <span className="ro-cl-deploy__meta">
            {[deploy.prefix, deploy.stage].filter(Boolean).join(' · ')}
            {deploy.notBefore && deploy.status === 'wacht' && (
                <> · vanaf {day(deploy.notBefore)}</>
            )}
        </span>
        <span className="ro-cl-bar" aria-hidden>
            <span
                style={{
                    width: `${
                        deploy.total ? (deploy.done / deploy.total) * 100 : 0
                    }%`,
                }}
            />
        </span>
        <span className="ro-cl-deploy__count">
            {deploy.done}/{deploy.total}
        </span>
    </Link>
);

/** How to let Claude follow this list, with the command to copy. */
const ClaudeFollow = () => {
    const [copied, setCopied] = useState(false);

    return (
        <section className="ro-card ro-claude" aria-label="Automatisch met Claude">
            <h3>Automatisch met Claude</h3>
            <ol>
                {CLAUDE_FOLLOW_STEPS.map((s) => (
                    <li key={s}>{s}</li>
                ))}
            </ol>
            <div className="ro-claude__cmd">
                <code className="ro-mono">{CLAUDE_FOLLOW_PROMPT}</code>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={async () => {
                        await navigator.clipboard?.writeText(CLAUDE_FOLLOW_PROMPT);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                    }}
                >
                    {copied ? 'Gekopieerd' : 'Kopieer'}
                </button>
            </div>
        </section>
    );
};

/**
 * The rollout as a checklist: every deploy run in order, and per deploy the steps before, during
 * and after it. Measured steps fill in by themselves; Claude ticks the MCP steps through the c2e-ops
 * MCP and a person the rest, here.
 */
export const RolloutChecklist = () => {
    const { deployId } = useParams();
    const navigate = useNavigate();
    const axios = useAxiosSwagger();
    const now = useNow();
    const { overview, live, error, apply } = useRolloutChecklist(deployId);
    const [busy, setBusy] = useState<string>();
    const [tickError, setTickError] = useState<string>();

    // Without a deploy in the address, open the one that is next.
    useEffect(() => {
        if (!deployId && overview?.next)
            navigate(checklistPath(overview.next), { replace: true });
    }, [deployId, overview?.next, navigate]);

    const deploy = useMemo(
        () => overview?.deploys.find((d) => d.id === deployId),
        [overview, deployId]
    );

    const tick = async (step: ChecklistStep, done: boolean, note?: string) => {
        if (!axios || !deploy) return;

        setBusy(step.key);
        setTickError(undefined);

        try {
            apply(
                await tickChecklistStep(
                    axios,
                    deploy.id,
                    step.key,
                    done,
                    note?.trim() || undefined
                )
            );
        } catch (e) {
            setTickError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(undefined);
        }
    };

    return (
        <main className="ro ro-cl">
            <header className="ro-banner">
                <div>
                    <h2>Afvinklijst</h2>
                    <p>{overview?.title ?? 'Uitrol'}</p>
                </div>
                <div className="ro-banner__side">
                    <span className={`ro-live${live ? ' is-on' : ''}`}>
                        {live ? 'Live' : 'Niet verbonden'}
                    </span>
                    <Link
                        to="/rollout"
                        className="btn btn-sm btn-outline-light"
                    >
                        Omgevingen
                    </Link>
                </div>
            </header>

            {error && <p className="ro-error">{error}</p>}

            <ClaudeFollow />

            <div className="ro-cl__layout">
                <nav className="ro-cl__deploys" aria-label="Deploys">
                    {overview?.deploys.map((d) => (
                        <DeployItem
                            key={d.id}
                            deploy={d}
                            active={d.id === deployId}
                            next={d.id === overview.next}
                        />
                    ))}
                </nav>

                <section
                    className="ro-card ro-cl__deploy"
                    aria-label={deploy?.title}
                >
                    {!deploy ? (
                        <div className="ro-empty">
                            {overview ? 'Kies een deploy' : 'Laden…'}
                        </div>
                    ) : (
                        <>
                            <header className="ro-cl__head">
                                <div>
                                    <h3>{deploy.title}</h3>
                                    {deploy.why && (
                                        <p className="ro-muted">{deploy.why}</p>
                                    )}
                                </div>
                                <dl className="ro-cl__facts">
                                    {deploy.environments.length > 0 && (
                                        <>
                                            <dt>Omgevingen</dt>
                                            <dd>
                                                {deploy.environments.join(', ')}
                                            </dd>
                                        </>
                                    )}
                                    {deploy.tickets.length > 0 && (
                                        <>
                                            <dt>Tickets</dt>
                                            <dd>
                                                {deploy.tickets
                                                    .map((t) => `#${t}`)
                                                    .join(', ')}
                                            </dd>
                                        </>
                                    )}
                                    {deploy.notBefore && (
                                        <>
                                            <dt>Vanaf</dt>
                                            <dd>{day(deploy.notBefore)}</dd>
                                        </>
                                    )}
                                    <dt>Gemeten</dt>
                                    <dd>
                                        {deploy.checkedAt
                                            ? `${ago(
                                                  deploy.checkedAt,
                                                  now
                                              )} · elke ${span(20000)}`
                                            : '–'}
                                    </dd>
                                </dl>
                            </header>

                            {tickError && (
                                <p className="ro-error">{tickError}</p>
                            )}

                            {PHASES.map((phase) => {
                                const steps = deploy.steps.filter(
                                    (s) => s.phase === phase.id
                                );

                                return steps.length === 0 ? null : (
                                    <div
                                        key={phase.id}
                                        className="ro-cl__phase"
                                    >
                                        <h4>
                                            {deploy.stage
                                                ? phase.label
                                                : phase.plain}
                                            <span className="ro-muted">
                                                {
                                                    steps.filter(
                                                        (s) =>
                                                            s.state === 'Done'
                                                    ).length
                                                }
                                                /{steps.length}
                                            </span>
                                        </h4>
                                        <ul>
                                            {steps.map((s) => (
                                                <StepRow
                                                    key={s.key}
                                                    step={s}
                                                    now={now}
                                                    busy={busy === s.key}
                                                    onTick={(done, note) =>
                                                        tick(s, done, note)
                                                    }
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </section>
            </div>
        </main>
    );
};
